/**
 * IdentityMethodNode
 *
 * Represents a single identity verification method inside the Identity Proof group.
 * There are four possible types: Government ID, KBA, Bank, and Selfie.
 *
 * - Government ID, KBA, and Bank are arranged horizontally (left to right).
 *   They are connected to each other with AND/OR logical edges (via LogicalEdge),
 *   reflecting the workflow's verification_option setting (BOTH = AND, EITHER = OR).
 *
 * - Selfie is placed below the method row and is always connected from the last
 *   method in the row with an AND edge, as selfie is a required follow-up.
 *
 * Position in flowchart (inside GroupNode):
 *   [GovtID] ──AND/OR── [KBA] ──AND/OR── [Bank]
 *                          ↓ (AND)
 *                       [Selfie]
 *                          ↓
 *                  [FaceMatch Decision]
 *
 * Icon is resolved dynamically from ICON_MAP based on data.type.
 *
 * Handles (all hidden — connections are defined in utils.js):
 *   - target-left / source-right: used for horizontal AND/OR chaining between methods
 *   - target-top / source-bottom: used for vertical connections (e.g. to Selfie, or from group entry)
 *
 * Data props:
 *   - type:  key identifying the method ('govt_id' | 'kba' | 'bank' | 'selfie')
 *   - label: display name shown next to the icon (e.g. "Government ID", "KBA")
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

import { ReactComponent as GovtId } from 'asset/CustomIcons/icon-government-id.svg';
import { ReactComponent as Liveness } from 'asset/CustomIcons/icon-liveness.svg';
import { ReactComponent as Bank } from 'asset/IconsPack/at_bank-filled.svg';
import { ReactComponent as Kba } from 'asset/IconsPack/at_file-question-02-filled.svg';

const ICON_MAP = {
    govt_id: GovtId,
    kba: Kba,
    bank: Bank,
    selfie: Liveness,
};

function IdentityMethodNode({ data }) {
    const Icon = ICON_MAP[data.type] || GovtId;

    return (
        <Box className={styles.identityMethodNode}>
            <Box className={styles.identityIconWrapper}>
                <Icon className={styles.identityIcon} />
            </Box>
            <Typography component="span" className={styles.identityMethodLabel}>
                {data.label}
            </Typography>

            {/* Left/Right handles: used for horizontal AND/OR chaining between method nodes */}
            <Handle
                type="target"
                position={Position.Left}
                id="target-left"
                style={{ opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Right}
                id="source-right"
                style={{ opacity: 0 }}
            />

            {/* Top/Bottom handles: used for vertical connections (e.g. last method → Selfie) */}
            <Handle type="target" position={Position.Top} id="target-top" style={{ opacity: 0 }} />
            <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ opacity: 0 }}
            />
        </Box>
    );
}

IdentityMethodNode.propTypes = {
    data: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }).isRequired,
};

export default IdentityMethodNode;
